import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { gql, useQuery } from "@apollo/client";
import StarekContext from "../context/StarekContext";

/**
 * Setting codes read outside the Settings page. The rows are copied into the
 * tenant from the platform defaults on every login; a user's saved Preference
 * overrides the row's meta.default. Units are the ones the seed uses (ms, %).
 */
export const SETTING = {
    DEVICE_LIST_POLLING_MS: "system.ui.device.list.polling_interval",
    DEVICE_HISTORY_WINDOW_MS: "system.ui.device.sparkline.duration",
    HEARTBEAT_ACTIVE_MS: "system.device.heartbeat_must",
    HEARTBEAT_INACTIVE_MS: "system.device.heartbeat_render_inactive",
    HEARTBEAT_UNRESPONSIVE_MS: "system.device.heartbeat_render_unresponsive",
    BATTERY_WARNING_PCT: "system.ui.device.battery.threshold_warning",
    BATTERY_CRITICAL_PCT: "system.ui.device.battery.threshold_critical",
    REVERSE_GEOCODING: "system.device.geolocation.reverse_geocoding_enabled",
    MAP_ZOOM: "system.device.map.zoom_level",
    REPORT_DEFAULT_RANGE: "system.ui.report.default_date_range",
    REPORT_AUTO_REFRESH: "system.ui.report.auto_refresh.enabled",
    REPORT_AUTO_REFRESH_MS: "system.ui.report.auto_refresh.interval",
    RAW_ON_OFF_ANALOG_AXIS: "system.ui.report.raw_on_off.show_analog_axis",
    RAW_ON_OFF_DIGITAL_AXIS: "system.ui.report.raw_on_off.show_digital_axis",
    TOAST_DURATION_MS: "system.ui.notification.toast.duration",
};

// Same selection and arguments as the Settings page query, so both read one
// cache entry and a save there (which refetches it) reaches every reader here.
const GET_SETTINGS = gql`
    query GetEffectiveSettings($userId: Int!) {
        settings {
            id
            code
            name
            meta
            user_preferences(where: { owned_by_id: { equals: $userId } }) {
                value
                owned_by {
                    name
                }
            }
        }
    }
`;

/**
 * Preferences are stored as strings ("true", "60000"); seeded defaults are JSON
 * values. Undefined for anything that does not fit the setting's type or its
 * min/max, so a bad value falls through to the next source instead of, say,
 * polling every 0 ms.
 */
export const parseValue = (raw, meta) => {
    if (raw === undefined || raw === null || raw === "") return undefined;
    switch (meta?.type) {
        case "integer": {
            const n = parseInt(raw, 10);
            if (Number.isNaN(n)) return undefined;
            const { min, max } = meta.validation ?? {};
            if (min !== undefined && n < min) return undefined;
            if (max !== undefined && n > max) return undefined;
            return n;
        }
        case "boolean":
            if (raw === true || raw === "true") return true;
            if (raw === false || raw === "false") return false;
            return undefined;
        default:
            return String(raw);
    }
};

/**
 * The signed-in user's effective settings: `get(code, fallback)` returns their
 * saved value, else the setting's default, else `fallback` (used until the query
 * loads, when it fails, or when the tenant has no such row).
 */
export default function useSettings() {
    const starekContext = useContext(StarekContext);
    const userId = parseInt(starekContext?.user?.id, 10);
    const client = starekContext?.engineClient;

    const { data, loading, error } = useQuery(GET_SETTINGS, {
        client,
        variables: { userId },
        skip: !userId || !client,
    });

    const values = useMemo(() => {
        const map = {};
        (data?.settings ?? []).forEach((s) => {
            const value =
                parseValue(s.user_preferences?.[0]?.value, s.meta) ??
                parseValue(s.meta?.default, s.meta);
            if (value !== undefined) map[s.code] = value;
        });
        return map;
    }, [data]);

    const get = useCallback(
        (code, fallback) => (code in values ? values[code] : fallback),
        [values],
    );

    return { get, ready: !userId || !client || !!data || !!error || !loading };
}

export function useSetting(code, fallback) {
    return useSettings().get(code, fallback);
}

/**
 * State that starts at `seed` and keeps following it (e.g. once settings load)
 * until the user sets it themselves - for on-screen toggles whose starting
 * position comes from a setting.
 */
export function useSeededState(seed) {
    const [value, setValue] = useState(seed);
    const touched = useRef(false);
    useEffect(() => {
        if (!touched.current) setValue(seed);
    }, [seed]);
    const set = useCallback((next) => {
        touched.current = true;
        setValue(next);
    }, []);
    return [value, set];
}

export function useSettingState(code, fallback) {
    return useSeededState(useSetting(code, fallback));
}

/**
 * Last-seen age limits in seconds. Active: heard from within the heartbeat
 * interval. Inactive: silent past `inactiveSecs`. Unresponsive: silent past
 * `unresponsiveSecs`.
 */
export function useHeartbeatThresholds() {
    const { get } = useSettings();
    return useMemo(
        () => ({
            activeSecs: get(SETTING.HEARTBEAT_ACTIVE_MS, 60000) / 1000,
            inactiveSecs: get(SETTING.HEARTBEAT_INACTIVE_MS, 300000) / 1000,
            unresponsiveSecs: get(SETTING.HEARTBEAT_UNRESPONSIVE_MS, 3600000) / 1000,
        }),
        [get],
    );
}

/** Battery levels (%): below `warning` is low, below `critical` is critical. */
export function useBatteryThresholds() {
    const { get } = useSettings();
    return useMemo(
        () => ({
            warning: get(SETTING.BATTERY_WARNING_PCT, 75),
            critical: get(SETTING.BATTERY_CRITICAL_PCT, 25),
        }),
        [get],
    );
}
